import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Copy, Check, Plus, X, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';

interface PanRecord {
  id: string;
  holderName: string;
  companyName: string | null;
  panNumber: string;
  notes: string | null;
}

const PAN_RX = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;

/**
 * Dashboard "sticky note" — a searchable directory of repeat customers'
 * and companies' PAN numbers, for quick lookup + copy when declaring a
 * PAN on a booking. Searchable by holder name or company name.
 */
export function PanDirectoryCard() {
  const qc = useQueryClient();
  const list = useQuery<PanRecord[]>({
    queryKey: ['pan-records'],
    queryFn: () => api('/api/pan-records'),
  });
  const [query, setQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const records = list.data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return records;
    return records.filter(
      (r) =>
        r.holderName.toLowerCase().includes(q) ||
        (r.companyName ?? '').toLowerCase().includes(q) ||
        r.panNumber.toLowerCase().includes(q),
    );
  }, [records, query]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['pan-records'] });

  const copy = (rec: PanRecord) => {
    void navigator.clipboard.writeText(rec.panNumber).catch(() => {});
    setCopiedId(rec.id);
    setTimeout(() => setCopiedId((c) => (c === rec.id ? null : c)), 1500);
  };

  const remove = async (rec: PanRecord) => {
    try {
      await api(`/api/pan-records/${rec.id}`, { method: 'DELETE' });
      refresh();
    } catch {
      /* surfaced by the next refetch */
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-700/40 dark:bg-amber-900/10">
      <CardHeader>
        <CardTitle className="text-base">Customer PAN directory</CardTitle>
        <CardDescription>Quick PAN lookup for bookings — search by customer or company.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer or company…"
            className="h-9 w-full rounded-lg border border-amber-200 bg-white pl-8 pr-3 text-sm outline-none focus:border-amber-400 dark:border-amber-700/40 dark:bg-zinc-900"
          />
        </div>

        <div className="mt-3 max-h-[320px] space-y-2 overflow-y-auto">
          {list.isLoading ? (
            <p className="py-6 text-center text-xs text-muted-foreground">
              <Loader2 className="mx-auto mb-1 h-4 w-4 animate-spin" />
              Loading…
            </p>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">
              {records.length === 0
                ? 'No PANs yet — add your first repeat customer below.'
                : 'No match. Try a different name.'}
            </p>
          ) : (
            filtered.map((rec) => (
              <div
                key={rec.id}
                className="group rounded-lg border border-amber-200 bg-white px-3 py-2 dark:border-amber-700/40 dark:bg-zinc-900"
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">{rec.holderName}</p>
                    {rec.companyName ? (
                      <p className="truncate text-xs text-muted-foreground">{rec.companyName}</p>
                    ) : null}
                    <p className="mt-0.5 font-mono text-sm font-bold tracking-wider text-amber-900 dark:text-amber-300">
                      {rec.panNumber}
                    </p>
                    {rec.notes ? (
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{rec.notes}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => copy(rec)}
                      title="Copy PAN"
                      className="rounded-md border border-amber-200 bg-amber-50 p-1.5 text-amber-800 hover:bg-amber-100 dark:border-amber-700/40 dark:bg-amber-900/20"
                    >
                      {copiedId === rec.id ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(rec)}
                      title="Remove"
                      className="rounded-md p-1.5 text-zinc-300 opacity-0 transition-opacity hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {adding ? (
          <AddPanForm
            onClose={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              refresh();
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-amber-300 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100/60 dark:border-amber-700/50 dark:text-amber-300"
          >
            <Plus className="h-3.5 w-3.5" />
            Add a PAN
          </button>
        )}
      </CardContent>
    </Card>
  );
}

function AddPanForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [holderName, setHolderName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const panOk = PAN_RX.test(panNumber.trim());
  const canSave = holderName.trim().length > 0 && panOk && !busy;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/api/pan-records', {
        method: 'POST',
        body: {
          holderName: holderName.trim(),
          companyName: companyName.trim() || null,
          panNumber: panNumber.trim().toUpperCase(),
        },
      });
      onSaved();
    } catch {
      setErr('Could not save — check the PAN format (AAAAA9999A).');
    } finally {
      setBusy(false);
    }
  };

  const field =
    'h-9 w-full rounded-lg border border-amber-200 bg-white px-3 text-sm outline-none focus:border-amber-400 dark:border-amber-700/40 dark:bg-zinc-900';

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-white p-3 dark:border-amber-700/40 dark:bg-zinc-900">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-amber-900 dark:text-amber-300">New PAN</span>
        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <input
        value={holderName}
        onChange={(e) => setHolderName(e.target.value)}
        placeholder="Customer / holder name"
        className={field}
      />
      <input
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        placeholder="Company name (optional)"
        className={field}
      />
      <input
        value={panNumber}
        onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
        placeholder="PAN — AAAAA9999A"
        maxLength={10}
        className={`${field} font-mono tracking-wider ${
          panNumber && !panOk ? 'border-rose-300 focus:border-rose-400' : ''
        }`}
      />
      {err ? <p className="text-[11px] text-rose-700">{err}</p> : null}
      <button
        type="button"
        onClick={() => void save()}
        disabled={!canSave}
        className="w-full rounded-lg bg-amber-600 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save PAN'}
      </button>
    </div>
  );
}
